/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useTrackModuleView, useTrackSearch } from "@/lib/activityTracker";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, BookOpen, ArrowRight, Settings, Heart } from "lucide-react";
import { BP_CATEGORIES, BP_LEVELS, getCategoryMeta, getLevelMeta, DEFAULT_BP_ICON } from "@/lib/bestPracticesMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;

const isArabicLang = (lang) => lang === "ar" || lang === "tn";
const pick = (item, field, lang) => {
  if (isArabicLang(lang) && item[`${field}_ar`]) return item[`${field}_ar`];
  return item[field];
};

export default function BestPractices() {
  const { user, loading } = useAuth();
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [busy, setBusy] = useState(true);

  useTrackModuleView("best_practices");
  useTrackSearch("best_practices", search);

  useEffect(() => {
    if (loading) return;
    setBusy(true);
    const params = new URLSearchParams();
    if (filterCat !== "all") params.set("categorie", filterCat);
    if (filterLevel !== "all") params.set("niveau", filterLevel);
    if (search.trim()) params.set("q", search.trim());
    if (favoritesOnly) params.set("favorites_only", "true");
    params.set("limit", PAGE_SIZE);
    params.set("offset", page * PAGE_SIZE);
    axios.get(`${API}/best-practices?${params}`, { withCredentials: true })
      .then((res) => {
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
        setHasMore(res.data?.has_more || false);
      })
      .catch(() => setItems([]))
      .finally(() => setBusy(false));
  }, [loading, page, filterCat, filterLevel, search, favoritesOnly]);

  // Reset to page 0 when filters/search change
  useEffect(() => { setPage(0); }, [filterCat, filterLevel, search, favoritesOnly]);

  const filtered = useMemo(() => items, [items]);
  const isOwner = user?.role === "owner";
  const isAr = isArabicLang(lang);

  return (
    <DashboardShell allowedRoles={["client", "owner", "trainer"]}>
      <header className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            <BookOpen size={14} /> {isAr ? "البطاقات التقنية" : "Fiches techniques"}
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
            {isAr ? "أفضل الممارسات في تربية الماشية" : "Bonnes pratiques d'élevage"}
          </h1>
          <p className="mt-1 text-[#5C6B5C]">
            {isAr
              ? "مكتبة مصورة : الصحة، التغذية، التكاثر، الولادة، الإسعافات الأولية…"
              : "Bibliothèque illustrée : santé, alimentation, reproduction, agnelage, premiers secours…"}
          </p>
        </div>
        {isOwner && (
          <Link to="/dashboard/owner/best-practices">
            <Button data-testid="bp-admin-link" variant="outline" className="border-[#1C3A27]/30">
              <Settings size={14} className="me-1.5" /> Administrer
            </Button>
          </Link>
        )}
      </header>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-6 relative">
          <Search size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]" />
          <Input
            data-testid="bp-search"
            placeholder={isAr ? "ابحث (تطعيم، ولادة، ديدان…)" : "Rechercher (vaccin, agnelage, vermifuge…)"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ps-10 h-11 bg-white border-[#E2E4DF] rounded-xl"
          />
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          <Button
            data-testid="bp-fav-filter"
            variant={favoritesOnly ? "default" : "outline"}
            onClick={() => setFavoritesOnly((v) => !v)}
            className={favoritesOnly ? "bg-[#E05A3D] hover:bg-[#C84B31]" : "border-[#E2E4DF]"}
          >
            <Heart size={14} fill={favoritesOnly ? "white" : "transparent"} className="me-1.5" />
            {isAr ? "المفضلات" : "Favoris"}
          </Button>
          <span className="text-xs text-[#5C6B5C]">{total} {isAr ? "بطاقة" : "fiches"}</span>
        </div>

        <div className="md:col-span-12 flex flex-wrap gap-2">
          <Pill active={filterCat === "all"} onClick={() => setFilterCat("all")}>{isAr ? "الكل" : "Toutes"}</Pill>
          {BP_CATEGORIES.map((c) => (
            <Pill
              key={c.id}
              active={filterCat === c.id}
              color={c.color}
              onClick={() => setFilterCat(c.id === filterCat ? "all" : c.id)}
              testId={`bp-cat-${c.id}`}
            >
              <c.icon size={12} className="me-1" /> {c.label}
            </Pill>
          ))}
        </div>
        <div className="md:col-span-12 flex flex-wrap gap-2 -mt-1">
          <span className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] me-1 self-center">{isAr ? "المستوى :" : "Niveau :"}</span>
          <Pill active={filterLevel === "all"} onClick={() => setFilterLevel("all")}>{isAr ? "الكل" : "Tous"}</Pill>
          {BP_LEVELS.map((l) => (
            <Pill key={l.id} active={filterLevel === l.id} color={l.color} onClick={() => setFilterLevel(l.id === filterLevel ? "all" : l.id)} testId={`bp-level-${l.id}`}>{l.label}</Pill>
          ))}
        </div>
      </div>

      {busy ? (
        <p className="text-[#5C6B5C] py-12 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E2E4DF] bg-white p-10 text-center">
          <DEFAULT_BP_ICON size={36} className="mx-auto text-[#A8AFA1] mb-3" />
          <p className="font-bold text-[#1C3A27]">{isAr ? "لم يتم العثور على بطاقة" : "Aucune fiche trouvée"}</p>
          <p className="text-sm text-[#5C6B5C] mt-1">
            {total === 0 && !favoritesOnly
              ? "La bibliothèque est en cours de constitution. Revenez bientôt !"
              : "Essayez d'élargir vos filtres ou de modifier votre recherche."}
          </p>
        </div>
      ) : (
        <>
          <div data-testid="bp-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((it) => (
              <BestPracticeCard key={it.id} item={it} lang={lang} onFavToggled={() => {
                // refresh current page
                axios.get(`${API}/best-practices?favorites_only=${favoritesOnly}&offset=${page * PAGE_SIZE}&limit=${PAGE_SIZE}`, { withCredentials: true })
                  .then((res) => setItems(res.data?.items || []));
              }} />
            ))}
          </div>

          {(page > 0 || hasMore) && (
            <div className="mt-8 flex items-center justify-between gap-3">
              <Button variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="border-[#E2E4DF]">
                ← {isAr ? "السابق" : "Précédent"}
              </Button>
              <span className="text-sm text-[#5C6B5C]">Page {page + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
              <Button variant="outline" disabled={!hasMore} onClick={() => setPage((p) => p + 1)} className="border-[#E2E4DF]">
                {isAr ? "التالي" : "Suivant"} →
              </Button>
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}

function Pill({ active, onClick, color = "#1C3A27", children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border-2 transition-all ${
        active ? "text-white border-transparent shadow-sm" : "bg-white text-[#5C6B5C] border-[#E2E4DF] hover:border-[#1C3A27]/40"
      }`}
      style={active ? { backgroundColor: color } : undefined}
    >
      {children}
    </button>
  );
}

function BestPracticeCard({ item, lang, onFavToggled }) {
  const cat = getCategoryMeta(item.categorie);
  const lvl = getLevelMeta(item.niveau);
  const CatIcon = cat.icon;
  const titre = pick(item, "titre", lang);
  const resume = pick(item, "resume", lang);

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (item.is_favorite) {
        await axios.delete(`${API}/best-practices/${item.id}/favorite`, { withCredentials: true });
      } else {
        await axios.post(`${API}/best-practices/${item.id}/favorite`, {}, { withCredentials: true });
      }
      onFavToggled?.();
      toast.success(item.is_favorite ? "Retiré" : "Ajouté aux favoris ❤️");
    } catch { toast.error("Erreur"); }
  };

  return (
    <Link
      to={`/dashboard/best-practices/${item.id}`}
      data-testid={`bp-card-${item.id}`}
      className="group rounded-2xl bg-white border border-[#E2E4DF] overflow-hidden hover:border-[#E05A3D]/60 hover:shadow-lg transition-all flex flex-col"
    >
      <div
        className="aspect-[16/10] bg-gradient-to-br from-[#F0F0EB] to-[#E2E4DF] relative overflow-hidden"
        style={item.image ? { backgroundImage: `url(${item.image})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
      >
        {!item.image && (
          <div className="absolute inset-0 grid place-items-center text-[#A8AFA1]">
            <CatIcon size={48} style={{ color: cat.color, opacity: 0.4 }} />
          </div>
        )}
        <div className="absolute top-3 start-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: cat.color }}>
          <CatIcon size={11} /> {cat.label}
        </div>
        <div className="absolute top-3 end-3 flex gap-1.5">
          <button
            type="button"
            onClick={handleFav}
            data-testid={`bp-card-fav-${item.id}`}
            className={`w-8 h-8 rounded-full grid place-items-center shadow-sm transition-all ${
              item.is_favorite ? "bg-[#E05A3D] text-white" : "bg-white/90 text-[#1C3A27] hover:bg-white"
            }`}
            title="Favori"
          >
            <Heart size={14} fill={item.is_favorite ? "white" : "transparent"} />
          </button>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-sm" style={{ backgroundColor: lvl.color }}>
            {lvl.label}
          </span>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-display text-lg font-bold text-[#1C3A27] leading-tight mb-1.5 group-hover:text-[#E05A3D] transition">{titre}</h3>
        <p className="text-sm text-[#5C6B5C] line-clamp-3 flex-1">{resume}</p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#E05A3D] group-hover:gap-2.5 transition-all">
          {lang === "ar" || lang === "tn" ? "اقرأ البطاقة" : "Lire la fiche"} <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}
